<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import SettingGuide from '../../SettingGuide.svelte';
	import { ButtonV2, Container, Text } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { deviceHasSettingTitle } from 'src/helpers/settings/device_has_setting_title';
	import {
		deviceFirmwareAtLeast,
		EMETER_RESET_COUNTERS_MIN_VER,
		getEmeterDataRpcNamespace,
	} from 'src/helpers/settings/emeter_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';

	export let device: Device;

	const settings = readDeviceSettings(device.id);
	const dataNamespace = getEmeterDataRpcNamespace(device);
	const showResetCounters =
		deviceHasSettingTitle(device, 'reset_energy_counters') &&
		deviceFirmwareAtLeast(settings, EMETER_RESET_COUNTERS_MIN_VER);
	const showDelete = deviceHasSettingTitle(device, 'delete_stored_data');

	let resetLoading = false;
	let deleteLoading = false;

	const resetCounters = async () => {
		resetLoading = true;
		const res: SettingRequest = await gen2Request(device, `${dataNamespace}.ResetCounters`, {
			id: device.channel,
		});
		onSettingsResponse(res, device, {
			success: $format('toast_reset_energy_counters_success', {
				default: 'Energy totals were reset. History is unchanged.',
			}),
			danger: $format('error_global', { default: 'Something went wrong!' }),
		});
		resetLoading = false;
	};

	const deleteData = async () => {
		deleteLoading = true;
		const res: SettingRequest = await gen2Request(device, `${dataNamespace}.DeleteAllData`, {
			id: device.channel,
		});
		onSettingsResponse(res, device, {
			success: $format('erase_data_scc_toast', {
				default: "Device's data was erased successfully!",
			}),
			danger: $format('error_global', { default: 'Something went wrong!' }),
		});
		deleteLoading = false;
	};
</script>

<div class="emeter-stored-data">
	<SettingGuide
		text={$format('settings_guide_emeter_stored_data', {
			default:
				'Reset totals to zero while keeping history, or delete stored energy data on this device. Deleting cannot be undone.',
		})}
	/>

	{#if showResetCounters}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_reset_counters', {
					default: 'Reset counters',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_guide_reset_energy_counters', {
					default: 'Zero the running energy totals. Recorded history stays on the device.',
				})}
			</Text>
			<ButtonV2
				size="m"
				width="full"
				loading={resetLoading}
				disabled={resetLoading || deleteLoading}
				aria-label={$format('button_reset_energy_counters', {
					default: 'Reset counters',
				})}
				on:click={resetCounters}
			>
				{$format('button_reset_energy_counters', {
					default: 'Reset counters',
				})}
			</ButtonV2>
		</Container>
	{/if}

	{#if showDelete}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_emeter_delete_stored_data', {
					default: 'Delete stored data',
				})}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_guide_delete_stored_data', {
					default: 'Erase energy readings stored on this device. This cannot be undone.',
				})}
			</Text>
			<ButtonV2
				size="m"
				width="full"
				variant="danger"
				loading={deleteLoading}
				disabled={deleteLoading || resetLoading}
				aria-label={$format('button_delete_stored_energy_data', {
					default: 'Delete stored data',
				})}
				on:click={deleteData}
			>
				{$format('button_delete', { default: 'Delete' })}
			</ButtonV2>
		</Container>
	{/if}
</div>

<style>
	.emeter-stored-data {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
